/**
 * GripMag — gripmag.js
 * Vanilla JS — no framework dependency.
 * Works with Shopify's AJAX Cart API endpoints.
 *
 * Modules:
 *  1. AJAX Cart  — POST /cart/add.js, GET /cart.js, cart count updates
 *  2. Product Gallery — thumbnail switching, image zoom
 *  3. Variant Selector — option buttons update hidden variant ID + price
 *  4. Quantity Stepper
 *  5. Sticky Buy Bar — IntersectionObserver
 *  6. Accordion
 *  7. Size-bar animation (home page)
 *  8. Hero parallax (home page)
 *  9. Cart Toast notification
 * 10. Category tab scroll-spy (collection page)
 */

(function () {
  'use strict';

  /* ──────────────────────────────
     UTILITY: fetch wrapper
  ────────────────────────────── */
  function ajax(url, opts) {
    return fetch(url, Object.assign({ headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }, opts))
      .then(function (r) { return r.json(); });
  }

  /* ──────────────────────────────
     1. AJAX CART
  ────────────────────────────── */
  function updateCartCount(count) {
    var el = document.getElementById('cart-count');
    if (!el) return;
    el.textContent = count > 0 ? '(' + count + ')' : '';
    el.dataset.cartCount = count;
  }

  function refreshCartCount() {
    ajax('/cart.js').then(function (cart) {
      updateCartCount(cart.item_count);
    }).catch(function () {});
  }

  /**
   * addToCart — POST to Shopify AJAX cart endpoint
   * @param {number} variantId  — Shopify variant ID
   * @param {number} qty        — quantity
   * @param {string} title      — product title for toast
   */
  function addToCart(variantId, qty, title) {
    if (!variantId) return;
    var btn = document.getElementById('buyBtn');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    ajax('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ id: variantId, quantity: qty || 1 })
    }).then(function () {
      return ajax('/cart.js');
    }).then(function (cart) {
      updateCartCount(cart.item_count);
      showToast('✓ ' + (title || 'Item') + ' added to cart');
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.61L23 6H6"/></svg> Buy Now';
      }
    }).catch(function (err) {
      console.error('Cart error:', err);
      showToast('Could not add to cart. Please try again.');
      if (btn) { btn.disabled = false; btn.textContent = 'Buy Now'; }
    });
  }

  /* ──────────────────────────────
     9. CART TOAST
  ────────────────────────────── */
  function showToast(msg) {
    var toast = document.getElementById('cart-toast');
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('show');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function () { toast.classList.remove('show'); }, 2600);
  }

  /* ──────────────────────────────
     PRODUCT FORM WIRING
  ────────────────────────────── */
  var productForm = document.getElementById('product-form');
  if (productForm) {

    // Intercept form submit → use AJAX
    productForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var variantId = parseInt(document.getElementById('variantId').value, 10);
      var qty       = parseInt(document.getElementById('qtyInput').value, 10) || 1;
      var title     = document.querySelector('.product-name') ? document.querySelector('.product-name').textContent.trim() : '';
      addToCart(variantId, qty, title);
    });

    // Sticky buy bar ATC
    var stickyBtn = document.querySelector('[data-sticky-atc]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        var variantId = parseInt(stickyBtn.dataset.variantId, 10);
        var title     = document.querySelector('.product-name') ? document.querySelector('.product-name').textContent.trim() : '';
        addToCart(variantId, 1, title);
      });
    }
  }

  /* ──────────────────────────────
     Product-card ATC buttons (collection page)
  ────────────────────────────── */
  document.querySelectorAll('.pc-atc').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var variantId = parseInt(btn.dataset.productId, 10);
      var title     = btn.dataset.productTitle || 'Item';
      addToCart(variantId, 1, title);
    });
  });

  /* ──────────────────────────────
     2. PRODUCT GALLERY — thumbnail switcher
  ────────────────────────────── */
  var mainImgEl = document.getElementById('mainImgEl');
  if (mainImgEl) {
    document.querySelectorAll('.thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        document.querySelectorAll('.thumb').forEach(function (t) {
          t.classList.remove('active');
          t.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('active');
        thumb.setAttribute('aria-pressed', 'true');

        var newSrc    = thumb.dataset.imageSrc;
        var newSrcset = thumb.dataset.imageSrcset;
        var newAlt    = thumb.dataset.imageAlt;
        if (newSrc) {
          mainImgEl.style.opacity = '0';
          setTimeout(function () {
            mainImgEl.src     = newSrc;
            if (newSrcset) mainImgEl.srcset = newSrcset;
            if (newAlt)    mainImgEl.alt    = newAlt;
            mainImgEl.style.opacity = '1';
          }, 160);
        }
      });
    });

    // Video thumb — open video URL
    document.querySelectorAll('[data-video]').forEach(function (el) {
      el.addEventListener('click', function () {
        var url = el.dataset.video;
        if (url) window.open(url, '_blank', 'noopener');
      });
    });
  }

  /* ──────────────────────────────
     3. VARIANT SELECTOR
     Reads all option buttons, computes selected variant,
     updates hidden #variantId, price display, and buy button state.
  ────────────────────────────── */
  var productData = window.__gripmag_product;  // injected by product-page.liquid via {{ product | json }}

  if (productData && productData.variants) {
    function getSelectedOptions() {
      var opts = {};
      document.querySelectorAll('[data-option-index]').forEach(function (btn) {
        if (btn.classList.contains('active') || btn.classList.contains('colour-swatch') && btn.classList.contains('active')) {
          opts[btn.dataset.optionIndex] = btn.dataset.value;
        }
      });
      return opts;
    }

    function findVariant(selectedOptions) {
      return productData.variants.find(function (v) {
        return Object.keys(selectedOptions).every(function (idx) {
          return v['option' + (parseInt(idx, 10) + 1)] === selectedOptions[idx];
        });
      });
    }

    function onOptionChange(clickedBtn) {
      var idx = clickedBtn.dataset.optionIndex;
      // Deactivate siblings
      document.querySelectorAll('[data-option-index="' + idx + '"]').forEach(function (b) {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      clickedBtn.classList.add('active');
      clickedBtn.setAttribute('aria-pressed', 'true');

      // Update selected-val label.
      var blocks = document.querySelectorAll('.options-block');
      var block  = blocks[parseInt(idx, 10)];
      var label  = block ? block.querySelector('.selected-val') : null;
      if (label) label.textContent = ' — ' + clickedBtn.dataset.value;

      var selected = getSelectedOptions();
      var variant  = findVariant(selected);
      if (!variant) return;

      // Update hidden input
      var vInput = document.getElementById('variantId');
      if (vInput) vInput.value = variant.id;

      // Update sticky bar variant
      var stickyBtn = document.querySelector('[data-sticky-atc]');
      if (stickyBtn) stickyBtn.dataset.variantId = variant.id;

      // Update price display
      var priceNow = document.getElementById('priceNow');
      var priceWas = document.getElementById('priceWas');
      if (priceNow) priceNow.textContent = formatMoney(variant.price);
      if (priceWas) {
        if (variant.compare_at_price > variant.price) {
          priceWas.textContent  = formatMoney(variant.compare_at_price);
          priceWas.style.display = '';
        } else {
          priceWas.style.display = 'none';
        }
      }

      // Update sticky bar price
      var sbPrice = document.querySelector('.sb-price');
      if (sbPrice) sbPrice.textContent = formatMoney(variant.price);

      // Update buy button availability
      var buyBtn = document.getElementById('buyBtn');
      if (buyBtn) {
        if (variant.available) {
          buyBtn.disabled = false;
          buyBtn.removeAttribute('aria-disabled');
        } else {
          buyBtn.disabled = true;
          buyBtn.setAttribute('aria-disabled', 'true');
          buyBtn.textContent = 'Sold Out';
        }
      }

      // Swap to variant image if available.
      // NOTE: the <img> still carries the srcset/sizes attributes rendered
      // at page load for the ORIGINAL featured image. Browsers give srcset
      // priority over src, so updating .src alone (as this used to do) had
      // no visible effect — the browser kept rendering whatever srcset
      // still pointed to. Must clear srcset/sizes whenever we set a new
      // plain .src, same as the working thumbnail-click handler above does.
      if (variant.featured_image && mainImgEl) {
        mainImgEl.style.opacity = '0';
        setTimeout(function () {
          mainImgEl.removeAttribute('srcset');
          mainImgEl.removeAttribute('sizes');
          mainImgEl.src = variant.featured_image.src;
          mainImgEl.alt = variant.featured_image.alt || '';
          mainImgEl.style.opacity = '1';
        }, 160);
      }

      // Update URL without reload
      if (history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }

    document.querySelectorAll('.size-opt, .colour-swatch').forEach(function (btn) {
      btn.addEventListener('click', function () { onOptionChange(btn); });
    });
  }

  function formatMoney(cents) {
    var rands = cents / 100;
    return 'R' + (rands % 1 === 0 ? rands.toFixed(0) : rands.toFixed(2));
  }

  /* ──────────────────────────────
     4. QUANTITY STEPPER
  ────────────────────────────── */
  document.querySelectorAll('.qty-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var inp = document.getElementById('qtyInput');
      if (!inp) return;
      var delta = parseInt(btn.dataset.qty, 10);
      var val   = parseInt(inp.value, 10) || 1;
      inp.value = Math.max(1, val + delta);
    });
  });

  /* ──────────────────────────────
     5. STICKY BUY BAR — IntersectionObserver
  ────────────────────────────── */
  var stickyBar = document.getElementById('stickyBar');
  var productTop = document.getElementById('productTop');
  if (stickyBar && productTop) {
    var barIO = new IntersectionObserver(function (entries) {
      stickyBar.classList.toggle('visible', !entries[0].isIntersecting);
    }, { rootMargin: '0px 0px -80% 0px' });
    barIO.observe(productTop);
  }

  /* ──────────────────────────────
     6. ACCORDION
  ────────────────────────────── */
  document.querySelectorAll('.acc-trigger').forEach(function (trigger) {
    trigger.addEventListener('click', function () {
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isOpen);
      var body = trigger.nextElementSibling;
      if (body) body.classList.toggle('open', !isOpen);
    });
  });

  /* ──────────────────────────────
     7. SIZE-BAR ANIMATION (home page)
  ────────────────────────────── */
  var sizeBars = document.querySelectorAll('[data-bar]');
  if (sizeBars.length) {
    var sizeSection = document.querySelector('[data-size-bars]') || sizeBars[0].closest('section');
    if (sizeSection) {
      var sizeIO = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          sizeBars.forEach(function (b) { b.classList.add('lit'); });
          sizeIO.disconnect();
        }
      }, { threshold: 0.4 });
      sizeIO.observe(sizeSection);
    }
  }

  /* ──────────────────────────────
     8. HERO PARALLAX (home page only)
  ────────────────────────────── */
  var heroImg = document.querySelector('.hero-img');
  if (heroImg && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.addEventListener('mousemove', function (e) {
      var nx = (e.clientX / window.innerWidth - 0.5) * 0.015;
      heroImg.style.transform = 'translateX(calc(4% + ' + (nx * -18) + 'px)) scale(1.02)';
    }, { passive: true });
  }

  /* ──────────────────────────────
     10. CATEGORY TAB SCROLL-SPY (collection page)
  ────────────────────────────── */
  var catTabs = document.querySelectorAll('.cat-tab[data-target]');
  if (catTabs.length) {
    var catSections = Array.from(catTabs).map(function (t) { return document.getElementById(t.dataset.target); });
    var catIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var idx = catSections.indexOf(entry.target);
          if (idx > -1) {
            catTabs.forEach(function (t) { t.classList.remove('active'); });
            catTabs[idx].classList.add('active');
          }
        }
      });
    }, { rootMargin: '-56px 0px -50% 0px', threshold: 0 });
    catSections.forEach(function (s) { if (s) catIO.observe(s); });

    catTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = document.getElementById(tab.dataset.target);
        if (!target) return;
        var offset = 56 + 44; // nav + cat-bar heights
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
      });
    });
  }

  /* ──────────────────────────────
     INIT — refresh cart count on load
  ────────────────────────────── */
  refreshCartCount();

  /* ──────────────────────────────
     HOME PAGE — add snap class to html
  ────────────────────────────── */
  if (document.querySelector('.snap-section')) {
    document.documentElement.classList.add('home-page');
  }

})();

/* ──────────────────────────────
   CARD SIZE SELECTOR
   Handles inline size buttons on collection product cards.
   When a size is clicked:
     • marks the button active
     • updates the displayed price and compare price
     • updates the card's Add button variant ID
     • swaps the card image if the variant has one
   All data lives on data-* attributes — no extra API calls needed.
────────────────────────────── */
(function () {
  document.querySelectorAll('.pc-size-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var cardId = btn.dataset.cardId;

      // Deactivate all siblings
      document.querySelectorAll('.pc-size-btn[data-card-id="' + cardId + '"]').forEach(function (b) {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');

      // Update price display
      var priceEl = document.getElementById('card-price-' + cardId);
      var wasEl   = document.getElementById('card-was-'   + cardId);
      if (priceEl && btn.dataset.price) {
        priceEl.textContent = btn.dataset.price;
      }
      if (wasEl) {
        if (btn.dataset.comparePrice && btn.dataset.comparePrice !== '') {
          wasEl.textContent    = btn.dataset.comparePrice;
          wasEl.style.display  = '';
        } else {
          wasEl.style.display  = 'none';
          wasEl.textContent    = '';
        }
      }

      // Update Add button's variant ID
      var atcBtn = document.getElementById('card-atc-' + cardId);
      if (atcBtn && btn.dataset.variantId) {
        atcBtn.dataset.variantId = btn.dataset.variantId;
        // Re-enable or disable based on availability
        if (btn.disabled) {
          atcBtn.disabled = true;
          atcBtn.setAttribute('aria-disabled', 'true');
          atcBtn.querySelector('svg') && (atcBtn.lastChild.textContent = 'Sold out');
        } else {
          atcBtn.disabled = false;
          atcBtn.removeAttribute('aria-disabled');
        }
      }

      // Swap card image if variant has one
      var imgEl = document.getElementById('card-img-' + cardId);
      if (imgEl && btn.dataset.image && btn.dataset.image !== '') {
        imgEl.style.opacity = '0';
        imgEl.style.transition = 'opacity .18s';
        setTimeout(function () {
          imgEl.src = btn.dataset.image;
          imgEl.style.opacity = '1';
        }, 180);
      }
    });
  });
})();

/* ──────────────────────────────
   CART DRAWER — full AJAX implementation
   Uses Shopify Cart API endpoints:
     GET  /cart.js           — fetch current cart state
     POST /cart/add.js       — add item(s)
     POST /cart/change.js    — update qty or remove
     POST /cart/clear.js     — empty cart
   Free shipping threshold read from window.__gripmag_settings.freeShipping (cents)
────────────────────────────── */
(function () {
  'use strict';

  var settings   = window.__gripmag_settings || {};
  var FREE_SHIP  = settings.freeShipping || 75000; // cents — R750 default
  var drawer     = document.getElementById('cartDrawer');
  var overlay    = document.getElementById('cartOverlay');
  var itemsEl    = document.getElementById('cartItems');
  var emptyEl    = document.getElementById('cartEmpty');
  var footerEl   = document.getElementById('cartFooter');
  var loadingEl  = document.getElementById('cartLoading');
  var subtotalEl = document.getElementById('cartSubtotal');
  var fillEl     = document.getElementById('shippingFill');
  var gapEl      = document.getElementById('shippingGap');
  var msgEl      = document.getElementById('shippingMsg');
  var progressEl = document.getElementById('shippingProgress');
  var countEl    = document.getElementById('cart-count');

  if (!drawer) return; // Not on a page with cart drawer

  /* ── open / close ── */
  function openDrawer() {
    drawer.classList.add('open');
    overlay.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    fetchAndRender();
  }
  function closeDrawer() {
    drawer.classList.remove('open');
    overlay.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.getElementById('cartClose').addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ── format money (ZAR) ── */
  function fmt(cents) {
    var rands = cents / 100;
    return 'R' + (rands % 1 === 0 ? rands.toFixed(0) : rands.toFixed(2));
  }

  /* ── fetch cart and render ── */
  function fetchAndRender() {
    showLoading();
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); })
      .catch(function () { showLoading(false); });
  }

  function showLoading(show) {
    if (show === false) {
      if (loadingEl) loadingEl.style.display = 'none';
    } else {
      if (loadingEl) loadingEl.style.display = 'flex';
      if (emptyEl)   emptyEl.hidden = true;
      if (footerEl)  footerEl.hidden = true;
    }
  }

  function renderCart(cart) {
    if (loadingEl) loadingEl.style.display = 'none';

    // Update nav count
    if (countEl) {
      countEl.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
      countEl.dataset.cartCount = cart.item_count;
    }

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '';
      if (emptyEl)  emptyEl.hidden = false;
      if (footerEl) footerEl.hidden = true;
      return;
    }

    if (emptyEl)  emptyEl.hidden = true;
    if (footerEl) footerEl.hidden = false;

    // Render items
    itemsEl.innerHTML = cart.items.map(function (item, i) {
      var imgHtml = item.image
        ? '<img src="' + item.image + '" alt="' + (item.product_title || '') + '" width="72" height="72" loading="lazy">'
        : '';
      var variantLabel = item.variant_title && item.variant_title !== 'Default Title'
        ? '<div class="cdi-variant">' + item.variant_title + '</div>'
        : '';
      return [
        '<div class="cd-item" data-key="' + item.key + '" data-index="' + (i+1) + '">',
          '<div class="cdi-img">' + imgHtml + '</div>',
          '<div class="cdi-body">',
            '<div class="cdi-title">' + item.product_title + '</div>',
            variantLabel,
            '<div class="cdi-price">' + fmt(item.final_line_price) + '</div>',
            '<div class="cdi-controls">',
              '<div class="cdi-qty">',
                '<button class="cdi-qty-btn" data-action="decrease" data-key="' + item.key + '" data-qty="' + item.quantity + '" aria-label="Decrease quantity">−</button>',
                '<span class="cdi-qty-num">' + item.quantity + '</span>',
                '<button class="cdi-qty-btn" data-action="increase" data-key="' + item.key + '" data-qty="' + item.quantity + '" aria-label="Increase quantity">+</button>',
              '</div>',
              '<button class="cdi-remove" data-key="' + item.key + '" aria-label="Remove ' + item.product_title + '">Remove</button>',
            '</div>',
          '</div>',
        '</div>'
      ].join('');
    }).join('');

    // Subtotal
    if (subtotalEl) subtotalEl.textContent = fmt(cart.total_price);

    // Free shipping bar
    var gap = FREE_SHIP - cart.total_price;
    var pct = Math.min(100, Math.round(cart.total_price / FREE_SHIP * 100));
    if (fillEl) fillEl.style.width = pct + '%';
    if (gap <= 0) {
      if (msgEl) msgEl.innerHTML = '<strong style="color:#4caf7d">✓ You qualify for free shipping!</strong>';
    } else {
      if (gapEl) gapEl.textContent = fmt(gap);
      if (msgEl && gapEl) {
        msgEl.innerHTML = 'Spend <strong>' + fmt(gap) + '</strong> more for free shipping';
      }
    }

    // Wire up qty / remove buttons
    itemsEl.querySelectorAll('.cdi-qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key    = btn.dataset.key;
        var action = btn.dataset.action;
        var qty    = parseInt(btn.dataset.qty, 10);
        var newQty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
        changeItem(key, newQty);
      });
    });
    itemsEl.querySelectorAll('.cdi-remove').forEach(function (btn) {
      btn.addEventListener('click', function () { changeItem(btn.dataset.key, 0); });
    });
  }

  function changeItem(key, qty) {
    showLoading();
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) { renderCart(cart); })
    .catch(function () { showLoading(false); });
  }

  /* ── Override addToCart to open drawer ── */
  var _origAddToCart = window.addToCart;
  window.addToCart = function (variantId, qty, title) {
    var btn = document.getElementById('buyBtn');
    if (btn) { btn.disabled = true; }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty || 1 })
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      openDrawer();
      if (btn) { btn.disabled = false; }
    })
    .catch(function (err) {
      console.error('Cart add error:', err);
      showToast('Could not add to cart. Please try again.');
      if (btn) { btn.disabled = false; }
    });
  };

  /* ── Override pc-atc click to open drawer ── */
  document.querySelectorAll('.pc-atc').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var variantId = parseInt(btn.dataset.variantId, 10);
      var title     = btn.dataset.productTitle || 'Item';
      window.addToCart(variantId, 1, title);
    });
  });

  /* ── Sticky bar ATC ── */
  var stickyAtcBtn = document.querySelector('[data-sticky-atc]');
  if (stickyAtcBtn) {
    stickyAtcBtn.addEventListener('click', function () {
      var variantId = parseInt(stickyAtcBtn.dataset.variantId, 10);
      var title = document.querySelector('.product-name')
        ? document.querySelector('.product-name').textContent.trim() : '';
      window.addToCart(variantId, 1, title);
    });
  }

  /* ── Expose openDrawer for nav cart icon ── */
  window.openCartDrawer = openDrawer;

  /* ── Cart icon in nav opens drawer ── */
  var navCartLink = document.querySelector('.sn-cart');
  if (navCartLink) {
    navCartLink.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer();
    });
  }

  /* ── Announcement bar height offset ── */
  var annBar = document.querySelector('.ann-bar');
  if (annBar) {
    document.documentElement.style.setProperty('--ann-bar-h', annBar.offsetHeight + 'px');
  }

})();


/* ──────────────────────────────
   ORDER BUMP — single silicone tray
   Checkbox in cart drawer footer adds/removes item via AJAX.
────────────────────────────── */
(function () {
  var bumped = false;

  document.addEventListener('change', function (e) {
    var check = e.target;
    if (!check || !check.classList.contains('ob-check')) return;

    var variantId = parseInt(check.dataset.variantId, 10);
    var title     = check.dataset.title || 'Order bump item';

    if (check.checked && !bumped) {
      bumped = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
      .then(function (r) { return r.json(); })
      .then(function () {
        // Re-render drawer to reflect new cart state
        if (typeof fetchAndRender === 'function') fetchAndRender();
        else window.location.reload();
        showToast('✓ ' + title + ' added');
      })
      .catch(function () { check.checked = false; bumped = false; });

    } else if (!check.checked && bumped) {
      bumped = false;
      // Find and remove via cart/change.js (set qty to 0 for this variant)
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var item = cart.items.find(function (i) { return i.variant_id === variantId; });
        if (!item) return;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
      })
      .then(function () {
        if (typeof fetchAndRender === 'function') fetchAndRender();
      });
    }
  });
})();
